import { useState } from "react";
import {
	Button,
	Typography,
	List,
	ListItem,
	IconButton,
	Tooltip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	makeStyles,
} from "@material-ui/core";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { Droppable, Draggable } from "react-beautiful-dnd";
import Item from "./Item";

const useStyles = makeStyles((theme) => ({
	card: {
		minWidth: 500,
		margin: ".5rem 0",
		"&:last-child": {
			paddingBottom: 0,
		},
	},
	list: {
		width: "100%",
		borderRadius: ".5rem",
		padding: 0,
	},
	title: {
		display: "flex",
		alignItems: "center",
	},
	mainLink: {
		cursor: "default",
		display: "block",
		borderRadius: ".5rem",
		margin: "1rem 0",
		border: "1px solid #bbb",
		background: "white",
		"&:hover": {
			background: theme.palette.grey["200"],
		},
	},
}));

function Column({ columns, column, items, index, dndData, setDndData }) {
	const classes = useStyles();
	const [openChildAdd, setOpenChildAdd] = useState(false);
	const [newChildLink, setNewChildLink] = useState();
	const [openEditMain, setOpenEditMain] = useState(false);
	const [editMain, setEditMain] = useState();

	const removeMainLink = (mainLinkTitle) => {
		const newMainLinks = dndData.mainLinks;
		delete newMainLinks[mainLinkTitle];
		const newMainLinkOrder = dndData.mainLinkOrder.filter((value) => {
			return value !== mainLinkTitle;
		});

		setDndData({
			...dndData,
			mainLinks: newMainLinks,
			mainLinkOrder: newMainLinkOrder,
		});
	};

	const addChildToState = (e) => {
		e.preventDefault();

		const newChildLinks = {
			...dndData.childLinks,
			[newChildLink.title]: {
				title: newChildLink.title,
				url: newChildLink.url,
				isSubLink: false,
			},
		};

		const childLinksOrder = dndData.mainLinks[column.title].childLinkTitles;
		childLinksOrder.push(newChildLink.title);

		const newDndState = {
			...dndData,
			childLinks: newChildLinks,
			mainLinks: {
				...dndData.mainLinks,
				[column.title]: {
					...dndData.mainLinks[column.title],
					childLinkTitles: childLinksOrder,
				},
			},
		};
		setDndData(newDndState);

		setOpenChildAdd(false);
	};

	const updateMainLinkState = (e) => {
		e.preventDefault();

		//Change old link title and url to the new ones
		const removedOldLink = dndData.mainLinks;
		delete removedOldLink[column.title];
		const withUpdatedMainLink = {
			...removedOldLink,
			[editMain.title]: {
				title: editMain.title,
				url: editMain.url,
				childLinkTitles: column.childLinkTitles,
			},
		};

		const editedMainLinkTitles = dndData.mainLinkOrder.map((item) =>
			item === column.title ? editMain.title : item
		);

		setDndData({
			...dndData,
			mainLinks: withUpdatedMainLink,
			mainLinkOrder: editedMainLinkTitles,
		});

		setOpenEditMain(false);
		setEditMain();
	};

	const onChangeNewChild = (e) => {
		setNewChildLink({ ...newChildLink, [e.target.name]: e.target.value });
	};

	const onChangeEditMain = (e) => {
		setEditMain({ ...editMain, [e.target.name]: e.target.value });
	};

	const handleCloseEditMain = () => {
		setOpenEditMain(false);
	};

	const handleClickOpenChildAdd = (e, mainLinkTitle) => {
		setOpenChildAdd(true);
	};

	const handleCloseChildAdd = () => {
		setOpenChildAdd(false);
	};

	return (
		<Draggable key={column.title} draggableId={column.title} index={index}>
			{(provided) => (
				<ListItem
					{...provided.draggableProps}
					innerRef={provided.innerRef}
					dense
					disableRipple
					className={classes.mainLink}
					button
					{...provided.dragHandleProps}
				>
					<div style={{ display: "flex", alignItems: "center" }}>
						<Typography className={classes.title} component="h2" variant="h5">
							<IconButton
								style={{ cursor: "grab", marginRight: ".5rem" }}
								aria-label="move list item"
							>
								<DragIndicatorIcon />
							</IconButton>
							{column.title}
						</Typography>
						<Typography style={{ marginLeft: "1rem" }}>{column.url}</Typography>
						<IconButton
							style={{ marginLeft: ".5rem" }}
							aria-label="edit main link"
							onClick={() => {
								setEditMain({ title: column.title, url: column.url });
								setOpenEditMain(true);
							}}
						>
							<EditIcon />
						</IconButton>
						<Dialog
							open={openEditMain}
							onClose={() => {
								handleCloseEditMain();
								setEditMain();
							}}
						>
							<form onSubmit={updateMainLinkState}>
								<DialogTitle id="addChildLink">Edit Main Link</DialogTitle>
								<DialogContent>
									<DialogContentText>
										Please add a title and a URL for your main link.
									</DialogContentText>
									<TextField
										value={editMain && editMain.title}
										onChange={onChangeEditMain}
										margin="dense"
										id="editedMainTitle"
										label="Title"
										type="text"
										name="title"
										fullWidth
										required
									/>
									<TextField
										value={editMain && editMain.url}
										margin="dense"
										onChange={onChangeEditMain}
										id="editedMainURL"
										label="URL"
										type="text"
										name="url"
										fullWidth
										required
									/>
								</DialogContent>
								<DialogActions>
									<Button
										onClick={() => {
											handleCloseEditMain();
										}}
										color="primary"
									>
										Cancel
									</Button>
									<Button color="primary" type="submit">
										Update
									</Button>
								</DialogActions>
							</form>
						</Dialog>
						<IconButton
							style={{ marginLeft: ".5rem" }}
							aria-label="delete main link"
							onClick={() => {
								removeMainLink(column.title);
							}}
						>
							<DeleteIcon />
						</IconButton>
						<Tooltip
							title="Add Child Link"
							aria-label="add child link"
							placement="top"
						>
							<IconButton
								onClick={(e) => {
									handleClickOpenChildAdd(e, column.title);
								}}
								style={{ marginLeft: ".5rem" }}
							>
								<AddCircleOutlineIcon />
							</IconButton>
						</Tooltip>
						<Dialog
							open={openChildAdd}
							onClose={() => {
								handleCloseChildAdd();
								setNewChildLink();
							}}
						>
							<form onSubmit={addChildToState}>
								<DialogTitle id="addChildLink">Add Child Link</DialogTitle>
								<DialogContent>
									<DialogContentText>
										Please add a title and a URL for your child link.
									</DialogContentText>
									<TextField
										autoFocus
										onChange={onChangeNewChild}
										margin="dense"
										id="newChildLinkTitle"
										label="Title"
										type="text"
										name="title"
										fullWidth
										required
									/>
									<TextField
										margin="dense"
										onChange={onChangeNewChild}
										id="newChildLinkURL"
										label="URL"
										type="text"
										name="url"
										fullWidth
										required
									/>
								</DialogContent>
								<DialogActions>
									<Button
										onClick={() => {
											handleCloseChildAdd();
											setNewChildLink();
										}}
										color="primary"
									>
										Cancel
									</Button>
									<Button color="primary" type="submit">
										Add
									</Button>
								</DialogActions>
							</form>
						</Dialog>
					</div>
					<Droppable droppableId={column.title} type="childLink">
						{(provided) => (
							<List
								ref={provided.innerRef}
								{...provided.droppableProps}
								className={classes.list}
								aria-label="links"
							>
								{items.map((item, i) => (
									//key must match draggableId
									<Item
										key={i}
										column={column}
										item={item}
										i={i}
										dndData={dndData}
										setDndData={setDndData}
									/>
								))}
								{provided.placeholder}
							</List>
						)}
					</Droppable>
					<div className={classes.childLinkAdd}></div>
				</ListItem>
			)}
		</Draggable>
	);
}

export default Column;
